import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Truck, Box, Loader2 } from 'lucide-react';

export default function Fleet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('power_unit');
  const [vehicles, setVehicles] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const canManage = ['developer', 'company_admin', 'der', 'owner_operator'].includes(user?.role);

  const load = () => { setVehicles(null); client.get(`/vehicles?unit_type=${tab}`).then((r) => setVehicles(r.data)).catch(() => setVehicles([])); };
  useEffect(load, [tab]);

  return (
    <AppShell title="Fleet">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}><TabsList><TabsTrigger value="power_unit" data-testid="fleet-power-units-tab"><Truck className="mr-1.5 h-4 w-4" />Power Units</TabsTrigger><TabsTrigger value="trailer" data-testid="fleet-trailers-tab"><Box className="mr-1.5 h-4 w-4" />Trailers</TabsTrigger></TabsList></Tabs>
        {canManage && <Button onClick={() => setAddOpen(true)} data-testid="fleet-add-button"><Plus className="mr-1.5 h-4 w-4" />Add Vehicle</Button>}
      </div>
      <Card className="overflow-hidden">
        {vehicles === null ? <div className="space-y-2 p-4">{[1,2,3].map(i=><Skeleton key={i} className="h-12" />)}</div> : vehicles.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No {tab === 'power_unit' ? 'power units' : 'trailers'} yet.</div>
        ) : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Unit #</TableHead><TableHead>Make / Model</TableHead><TableHead>Year</TableHead><TableHead>Assigned</TableHead><TableHead>Registration</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow key={v.vehicle_id} className="cursor-pointer" onClick={() => navigate(`/fleet/${v.vehicle_id}`)} data-testid={`vehicle-row-${v.vehicle_id}`}>
                  <TableCell className="font-medium">{v.unit_number}<div className="text-xs text-muted-foreground">{v.ownership}</div></TableCell>
                  <TableCell>{v.make} {v.model}<div className="text-xs text-muted-foreground">VIN …{v.vin?.slice(-6)}</div></TableCell>
                  <TableCell>{v.year}</TableCell>
                  <TableCell className="text-sm">{v.assigned_driver_name || '-'}</TableCell>
                  <TableCell><StatusBadge status={v.registration_state === 'expired' ? 'expired' : v.registration_state === 'expiring' ? 'expiring' : 'valid'} label={v.registration_expiry ? new Date(v.registration_expiry).toLocaleDateString() : '-'} /></TableCell>
                  <TableCell><StatusBadge status={v.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        )}
      </Card>
      <AddVehicleDialog open={addOpen} onOpenChange={setAddOpen} unitType={tab} onAdded={load} />
    </AppShell>
  );
}

function AddVehicleDialog({ open, onOpenChange, unitType, onAdded }) {
  const [f, setF] = useState({ unit_number: '', make: '', model: '', year: 2022, vin: '', plate: '', ownership: 'company', status: 'active' });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.unit_number) { toast.error('Unit number is required'); return; }
    setLoading(true);
    try { await client.post('/vehicles', { ...f, year: Number(f.year), unit_type: unitType }); toast.success('Vehicle added'); onAdded(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-vehicle-dialog"><DialogHeader><DialogTitle>Add {unitType === 'power_unit' ? 'Power Unit' : 'Trailer'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Unit # *</Label><Input value={f.unit_number} onChange={(e) => upd('unit_number', e.target.value)} className="mt-1" data-testid="add-vehicle-unit" /></div>
          <div><Label>Year</Label><Input type="number" value={f.year} onChange={(e) => upd('year', e.target.value)} className="mt-1" /></div>
          <div><Label>Make</Label><Input value={f.make} onChange={(e) => upd('make', e.target.value)} className="mt-1" /></div>
          <div><Label>Model</Label><Input value={f.model} onChange={(e) => upd('model', e.target.value)} className="mt-1" /></div>
          <div><Label>VIN</Label><Input value={f.vin} onChange={(e) => upd('vin', e.target.value)} className="mt-1" /></div>
          <div><Label>Ownership</Label><Select value={f.ownership} onValueChange={(v) => upd('ownership', v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="company">Company</SelectItem><SelectItem value="driver">Driver</SelectItem></SelectContent></Select></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="add-vehicle-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
